/**
 * RepConnect - Healthcare Professional Availability Management
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import {
  Save, Plus, X, AlertTriangle, Edit3, Trash2,
  Calendar, Copy, Clock, CheckCircle, Plane, MapPin
} from 'lucide-react';
import { customerDataService } from '../utils/supabaseData';
import { customerLocationService } from '../services/locationService';
import { logger } from '../utils/logger';

const AvailabilityView = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('availability');
  const [saveStatus, setSaveStatus] = useState('');

  // Location-based availability support
  const [customerLocations, setCustomerLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null); // Will be set to primary location on load
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Weekly availability state - one array per day
  const [weeklyAvailability, setWeeklyAvailability] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: []
  });

  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [timeSlotForm, setTimeSlotForm] = useState({
    startTime: '12:00',
    endTime: '13:00',
    label: 'Lunch',
    notes: ''
  });

  const [meetingPrefs, setMeetingPrefs] = useState({
    meetingDuration: '60',
    advanceNotice: '48',
    maxMeetingsPerWeek: '3',
    cancellationPolicy: '24'
  });

  // Blocked periods state for vacation/out-of-office
  const [blockedPeriods, setBlockedPeriods] = useState([]);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [blockForm, setBlockForm] = useState({
    title: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    allDay: true, // true = full day block, false = specific time range
    type: 'vacation', // 'vacation', 'out-of-office', 'personal', 'conference'
    reason: '',
    recurring: false
  });

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const commonTimeSlots = [
    { label: 'Breakfast', start: '08:00', end: '10:00' },
    { label: 'Lunch', start: '12:00', end: '13:00' },
    { label: 'Extended Lunch', start: '11:30', end: '14:00' },
    { label: 'Afternoon Coffee', start: '15:00', end: '16:00' },
    { label: 'Dinner', start: '18:00', end: '20:00' },
    { label: 'Evening', start: '18:30', end: '21:00' }
  ];

  // Load customer locations on mount
  useEffect(() => {
    if (currentUser?.id) {
      loadCustomerLocations();
    }
  }, [currentUser]);

  // Load availability when location changes
  useEffect(() => {
    if (currentUser?.id && !loadingLocations) {
      loadSavedAvailability();
    }
  }, [currentUser, selectedLocation, loadingLocations]);

  const loadCustomerLocations = async () => {
    try {
      setLoadingLocations(true);
      const locations = await customerLocationService.getCustomerLocations(currentUser.id);
      setCustomerLocations(locations || []);

      // Set primary location as default, or first location if no primary
      const primaryLocation = locations?.find(loc => loc.is_primary_location);
      const defaultLocation = primaryLocation || locations?.[0] || null;

      if (defaultLocation) {
        setSelectedLocation(defaultLocation);
        logger.debug('Set default location for availability', {
          locationId: defaultLocation.location_id,
          locationName: defaultLocation.practice_locations?.location_name,
          isPrimary: defaultLocation.is_primary_location
        });
      }

      logger.debug('Loaded customer locations for availability', { count: locations?.length || 0 });
    } catch (error) {
      logger.error('Error loading customer locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const loadSavedAvailability = async () => {
    if (!currentUser?.id || !selectedLocation?.location_id) return;

    try {
      logger.debug('📋 Loading availability from database', {
        userId: currentUser.id,
        locationId: selectedLocation.location_id,
        locationName: selectedLocation.practice_locations?.location_name
      });

      // Load location-specific availability only
      const data = await customerDataService.getAvailabilityByLocation(currentUser.id, selectedLocation.location_id);

      if (data) {
        logger.debug('✅ Loaded availability from database', {
          locationId: selectedLocation.location_id,
          locationName: selectedLocation.practice_locations?.location_name,
          hasTimeSlots: data.weekly_availability ? Object.values(data.weekly_availability).some(day => day.length > 0) : false
        });
        if (data.weekly_availability) setWeeklyAvailability(data.weekly_availability);
        if (data.meeting_preferences) setMeetingPrefs(data.meeting_preferences);
        if (data.blocked_periods) setBlockedPeriods(data.blocked_periods);
      } else {
        // No data found - reset to empty state
        logger.debug('⚠️ No availability data found for this location', {
          locationId: selectedLocation.location_id,
          locationName: selectedLocation.practice_locations?.location_name
        });
        setWeeklyAvailability({
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        });
      }
    } catch (error) {
      logger.error('❌ Error loading availability:', error);
    }
  };

  // Helper function for auto-saving availability (location-aware)
  const autoSaveAvailability = async (updatedWeeklyAvailability, updatedMeetingPrefs = meetingPrefs, updatedBlockedPeriods = blockedPeriods) => {
    if (!currentUser?.id) {
      logger.error('No current user ID for auto-save');
      return;
    }

    try {
      if (!selectedLocation?.location_id) {
        logger.warn('⚠️ Cannot auto-save: No location selected');
        return;
      }

      const availabilityData = {
        weekly_availability: updatedWeeklyAvailability,
        meeting_preferences: updatedMeetingPrefs,
        blocked_periods: updatedBlockedPeriods
      };

      // Save location-specific availability only
      logger.debug('💾 Auto-saving location-specific availability', {
        locationId: selectedLocation.location_id,
        locationName: selectedLocation.practice_locations?.location_name,
        hasTimeSlots: Object.values(updatedWeeklyAvailability).some(day => day.length > 0)
      });

      await customerDataService.saveLocationAvailability(
        currentUser.id,
        selectedLocation.location_id,
        availabilityData
      );
      logger.debug('✅ Auto-saved location-specific availability to database');
    } catch (error) {
      logger.error('❌ Error auto-saving:', error);
      console.error('Auto-save error details:', error);
    }
  };

  const saveAvailability = async () => {
    if (!currentUser?.id) {
      logger.error('No current user ID');
      return;
    }

    if (!selectedLocation?.location_id) {
      logger.error('No location selected');
      setSaveStatus('Please select a location first.');
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }

    try {
      logger.debug('💾 Saving availability to database', {
        locationId: selectedLocation.location_id,
        locationName: selectedLocation.practice_locations?.location_name
      });

      const availabilityData = {
        weekly_availability: weeklyAvailability,
        meeting_preferences: meetingPrefs,
        blocked_periods: blockedPeriods
      };

      // Save location-specific availability only
      await customerDataService.saveLocationAvailability(
        currentUser.id,
        selectedLocation.location_id,
        availabilityData
      );

      logger.debug('✅ Successfully saved availability to database');

      const locationName = selectedLocation.practice_locations?.location_name;
      setSaveStatus(`Availability saved for ${locationName}!`);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      logger.error('Error saving availability:', error);
      setSaveStatus('Error saving availability. Please try again.');
      setTimeout(() => setSaveStatus(''), 4000);
    }
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const validateTimeSlot = (startTime, endTime, dayKey, excludeId = null) => {
    const start = new Date(`2000-01-01T${startTime}:00`);
    const end = new Date(`2000-01-01T${endTime}:00`);
    
    // Check if end time is after start time
    if (end <= start) {
      return { isValid: false, error: 'End time must be after start time' };
    }
    
    // Check minimum duration (30 minutes)
    const duration = (end - start) / (1000 * 60);
    if (duration < 30) {
      return { isValid: false, error: 'Time slot must be at least 30 minutes long' };
    }
    
    // Check for overlaps with existing slots
    const daySlots = weeklyAvailability[dayKey] || [];
    for (const slot of daySlots) {
      if (excludeId && slot.id === excludeId) continue;
      
      const slotStart = new Date(`2000-01-01T${slot.startTime}:00`);
      const slotEnd = new Date(`2000-01-01T${slot.endTime}:00`);
      
      // Check for overlap
      if ((start < slotEnd && end > slotStart)) {
        return { isValid: false, error: 'Time slot overlaps with existing availability' };
      }
    }
    
    return { isValid: true };
  };

  const addTimeSlot = async () => {
    const validation = validateTimeSlot(timeSlotForm.startTime, timeSlotForm.endTime, selectedDay);

    if (!validation.isValid) {
      setSaveStatus(`Error: ${validation.error}`);
      setTimeout(() => setSaveStatus(''), 4000);
      return;
    }

    const newSlot = {
      id: Date.now().toString(),
      startTime: timeSlotForm.startTime,
      endTime: timeSlotForm.endTime,
      label: timeSlotForm.label || 'Available',
      notes: timeSlotForm.notes || '',
      location_id: selectedLocation?.location_id || null
    };

    const updatedAvailability = {
      ...weeklyAvailability,
      [selectedDay]: [...(weeklyAvailability[selectedDay] || []), newSlot].sort((a, b) =>
        a.startTime.localeCompare(b.startTime)
      )
    };

    setWeeklyAvailability(updatedAvailability);

    // Auto-save to database (location-aware)
    await autoSaveAvailability(updatedAvailability, meetingPrefs, blockedPeriods);

    setShowTimeSlotModal(false);
    resetTimeSlotForm();
    setSaveStatus('Time slot added!');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const updateTimeSlot = async () => {
    if (!editingSlot) return;

    const validation = validateTimeSlot(
      timeSlotForm.startTime,
      timeSlotForm.endTime,
      selectedDay,
      editingSlot.id
    );

    if (!validation.isValid) {
      setSaveStatus(`Error: ${validation.error}`);
      setTimeout(() => setSaveStatus(''), 4000);
      return;
    }

    const updatedAvailability = {
      ...weeklyAvailability,
      [selectedDay]: weeklyAvailability[selectedDay].map(slot =>
        slot.id === editingSlot.id
          ? {
              ...slot,
              startTime: timeSlotForm.startTime,
              endTime: timeSlotForm.endTime,
              label: timeSlotForm.label,
              notes: timeSlotForm.notes,
              location_id: selectedLocation?.location_id || slot.location_id || null
            }
          : slot
      ).sort((a, b) => a.startTime.localeCompare(b.startTime))
    };

    setWeeklyAvailability(updatedAvailability);

    // Auto-save to database (location-aware)
    await autoSaveAvailability(updatedAvailability, meetingPrefs, blockedPeriods);

    setShowTimeSlotModal(false);
    setEditingSlot(null);
    resetTimeSlotForm();
    setSaveStatus('Time slot updated!');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const deleteTimeSlot = async (dayKey, slotId) => {
    const updatedAvailability = {
      ...weeklyAvailability,
      [dayKey]: weeklyAvailability[dayKey].filter(slot => slot.id !== slotId)
    };

    setWeeklyAvailability(updatedAvailability);

    // Auto-save to database (location-aware)
    await autoSaveAvailability(updatedAvailability, meetingPrefs, blockedPeriods);

    setSaveStatus('Time slot removed.');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const openAddSlotModal = (dayKey) => {
    setSelectedDay(dayKey);
    resetTimeSlotForm();
    setEditingSlot(null);
    setShowTimeSlotModal(true);
  };

  const openEditSlotModal = (dayKey, slot) => {
    setSelectedDay(dayKey);
    setTimeSlotForm({
      startTime: slot.startTime,
      endTime: slot.endTime,
      label: slot.label,
      notes: slot.notes
    });
    setEditingSlot(slot);
    setShowTimeSlotModal(true);
  };

  const resetTimeSlotForm = () => {
    setTimeSlotForm({
      startTime: '12:00',
      endTime: '13:00',
      label: 'Lunch',
      notes: ''
    });
  };

  const copyToOtherDays = async (fromDay) => {
    const sourceSlots = weeklyAvailability[fromDay] || [];
    if (sourceSlots.length === 0) return;

    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const targetDays = weekdays.filter(day => day !== fromDay);

    if (window.confirm(`Copy ${sourceSlots.length} time slot(s) from ${fromDay} to all other weekdays?`)) {
      const updatedAvailability = { ...weeklyAvailability };
      targetDays.forEach(day => {
        updatedAvailability[day] = sourceSlots.map(slot => ({
          ...slot,
          id: `${slot.id}_${day}_${Date.now()}`
        }));
      });

      setWeeklyAvailability(updatedAvailability);

      // Auto-save to database (location-aware)
      await autoSaveAvailability(updatedAvailability, meetingPrefs, blockedPeriods);

      setSaveStatus(`Copied ${sourceSlots.length} time slot(s) to ${targetDays.length} other days!`);
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  const getTotalAvailableSlots = () => {
    return Object.values(weeklyAvailability).reduce((total, daySlots) => total + daySlots.length, 0);
  };

  // Blocked periods functions
  const addBlockedPeriod = async () => {
    const startDate = new Date(blockForm.startDate);
    const endDate = new Date(blockForm.endDate);

    // For same-day time blocks, end date equals start date
    if (blockForm.allDay && endDate < startDate) {
      setSaveStatus('Error: End date must be on or after start date');
      setTimeout(() => setSaveStatus(''), 4000);
      return;
    }

    // For time-specific blocks, validate times if on same day
    if (!blockForm.allDay) {
      if (!blockForm.startTime || !blockForm.endTime) {
        setSaveStatus('Error: Please specify start and end times');
        setTimeout(() => setSaveStatus(''), 4000);
        return;
      }
      if (blockForm.startDate === blockForm.endDate && blockForm.endTime <= blockForm.startTime) {
        setSaveStatus('Error: End time must be after start time');
        setTimeout(() => setSaveStatus(''), 4000);
        return;
      }
    }

    const newBlock = {
      id: Date.now().toString(),
      title: blockForm.title || `${blockForm.type.charAt(0).toUpperCase() + blockForm.type.slice(1)} Block`,
      startDate: blockForm.startDate,
      endDate: blockForm.endDate,
      startTime: blockForm.allDay ? null : blockForm.startTime,
      endTime: blockForm.allDay ? null : blockForm.endTime,
      allDay: blockForm.allDay,
      type: blockForm.type,
      reason: blockForm.reason,
      recurring: blockForm.recurring,
      createdAt: new Date().toISOString()
    };

    const updatedBlocks = [...blockedPeriods, newBlock].sort((a, b) =>
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

    setBlockedPeriods(updatedBlocks);

    // Auto-save to database (location-aware)
    await autoSaveAvailability(weeklyAvailability, meetingPrefs, updatedBlocks);

    setShowBlockModal(false);
    resetBlockForm();
    setSaveStatus('Blocked period added!');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const updateBlockedPeriod = async () => {
    if (!editingBlock) return;

    const startDate = new Date(blockForm.startDate);
    const endDate = new Date(blockForm.endDate);

    // For same-day time blocks, end date equals start date
    if (blockForm.allDay && endDate < startDate) {
      setSaveStatus('Error: End date must be on or after start date');
      setTimeout(() => setSaveStatus(''), 4000);
      return;
    }

    // For time-specific blocks, validate times if on same day
    if (!blockForm.allDay) {
      if (!blockForm.startTime || !blockForm.endTime) {
        setSaveStatus('Error: Please specify start and end times');
        setTimeout(() => setSaveStatus(''), 4000);
        return;
      }
      if (blockForm.startDate === blockForm.endDate && blockForm.endTime <= blockForm.startTime) {
        setSaveStatus('Error: End time must be after start time');
        setTimeout(() => setSaveStatus(''), 4000);
        return;
      }
    }

    const updatedBlocks = blockedPeriods.map(block =>
      block.id === editingBlock.id
        ? {
            ...block,
            title: blockForm.title || `${blockForm.type.charAt(0).toUpperCase() + blockForm.type.slice(1)} Block`,
            startDate: blockForm.startDate,
            endDate: blockForm.endDate,
            startTime: blockForm.allDay ? null : blockForm.startTime,
            endTime: blockForm.allDay ? null : blockForm.endTime,
            allDay: blockForm.allDay,
            type: blockForm.type,
            reason: blockForm.reason,
            recurring: blockForm.recurring
          }
        : block
    ).sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

    setBlockedPeriods(updatedBlocks);

    // Auto-save to database (location-aware)
    await autoSaveAvailability(weeklyAvailability, meetingPrefs, updatedBlocks);

    setShowBlockModal(false);
    setEditingBlock(null);
    resetBlockForm();
    setSaveStatus('Blocked period updated!');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const deleteBlockedPeriod = async (blockId) => {
    const updatedBlocks = blockedPeriods.filter(block => block.id !== blockId);
    setBlockedPeriods(updatedBlocks);

    // Auto-save to database (location-aware)
    await autoSaveAvailability(weeklyAvailability, meetingPrefs, updatedBlocks);

    setSaveStatus('Blocked period removed.');
    setTimeout(() => setSaveStatus(''), 2000);
  };

  const openAddBlockModal = () => {
    resetBlockForm();
    setEditingBlock(null);
    setShowBlockModal(true);
  };

  const openEditBlockModal = (block) => {
    setBlockForm({
      title: block.title,
      startDate: block.startDate,
      endDate: block.endDate,
      startTime: block.startTime || '',
      endTime: block.endTime || '',
      allDay: block.allDay !== false, // Default to true for backwards compatibility
      type: block.type,
      reason: block.reason,
      recurring: block.recurring
    });
    setEditingBlock(block);
    setShowBlockModal(true);
  };

  const resetBlockForm = () => {
    setBlockForm({
      title: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      allDay: true,
      type: 'vacation',
      reason: '',
      recurring: false
    });
  };

  const formatDate = (dateString) => {
    // Append T12:00:00 to avoid timezone issues when parsing YYYY-MM-DD format
    // Without this, dates are parsed as midnight UTC and can shift to previous day in US timezones
    const dateToUse = dateString.includes('T') ? dateString : `${dateString}T12:00:00`;
    const date = new Date(dateToUse);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getBlockTypeIcon = (type) => {
    switch (type) {
      case 'vacation':
        return <Plane className="w-4 h-4" />;
      case 'out-of-office':
        return <MapPin className="w-4 h-4" />;
      case 'conference':
        return <Calendar className="w-4 h-4" />;
      case 'personal':
        return <Clock className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  const getBlockTypeColor = (type) => {
    switch (type) {
      case 'vacation':
        return 'bg-blue-100 text-blue-800';
      case 'out-of-office':
        return 'bg-orange-100 text-orange-800';
      case 'conference':
        return 'bg-purple-100 text-purple-800';
      case 'personal':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Availability Management</h2>
        <p className="text-gray-600 mt-1">
          Configure your weekly availability and meeting preferences
        </p>
        
        {saveStatus && (
          <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
            saveStatus.includes('Error') 
              ? 'bg-red-50 border border-red-200 text-red-700' 
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}>
            {saveStatus.includes('Error') ? (
              <AlertTriangle className="w-5 h-5" />
            ) : (
              <CheckCircle className="w-5 h-5" />
            )}
            <span>{saveStatus}</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {[
            { id: 'availability', label: 'Weekly Availability', icon: <Calendar className="w-4 h-4" /> },
            { id: 'blocking', label: 'Time Blocking', icon: <Plane className="w-4 h-4" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Weekly Availability Tab */}
      {activeTab === 'availability' && (
        <div className="space-y-6">
          {/* Location Selector */}
          {customerLocations.length > 0 && (
            <div className="bg-white border border-gray-300 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>Set availability for:</span>
                </div>
              </label>
              <select
                value={selectedLocation?.id || ''}
                onChange={(e) => {
                  const location = customerLocations.find(loc => loc.id === e.target.value);
                  setSelectedLocation(location || null);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {customerLocations.map((location) => {
                  const practiceLoc = location.practice_locations;
                  return (
                    <option key={location.id} value={location.id}>
                      {practiceLoc?.location_name || 'Unnamed Location'}
                      {location.is_primary_location && ' ⭐ (Primary)'}
                      {' - '}
                      {practiceLoc?.city}, {practiceLoc?.state}
                    </option>
                  );
                })}
              </select>
              <p className="mt-2 text-xs text-gray-500">
                {selectedLocation?.practice_locations?.location_name
                  ? `Setting time availability for ${selectedLocation.practice_locations.location_name}`
                  : 'Please select a location to set availability'
                }
              </p>
            </div>
          )}

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">
                    {getTotalAvailableSlots()} Total Time Slot{getTotalAvailableSlots() !== 1 ? 's' : ''} Available
                  </p>
                  <p className="text-sm text-blue-700">
                    Configure your weekly availability for lunch meetings
                  </p>
                </div>
              </div>
              <button
                onClick={() => openAddSlotModal('monday')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-md whitespace-nowrap"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Add Time Slot</span>
              </button>
            </div>
          </div>

          {/* Weekly Schedule */}
          <div className="space-y-4">
            {daysOfWeek.map(day => {
              const daySlots = weeklyAvailability[day.key] || [];

              return (
                <div key={day.key} className={`bg-white rounded-lg shadow-md ${daySlots.length === 0 ? 'p-4' : 'p-6'}`}>
                  <div className={`flex items-center justify-between ${daySlots.length === 0 ? 'mb-0' : 'mb-4'}`}>
                    <div className="flex items-center space-x-3">
                      <h3 className={`font-semibold text-gray-800 ${daySlots.length === 0 ? 'text-base' : 'text-lg'}`}>{day.label}</h3>
                      {daySlots.length > 0 && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          {daySlots.length} slot{daySlots.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {daySlots.length === 0 ? (
                      <p className="text-sm text-gray-500">No availability set</p>
                    ) : (
                      <button
                        onClick={() => copyToOtherDays(day.key)}
                        className="flex items-center space-x-1 px-3 py-1 text-gray-600 hover:text-blue-600 border border-gray-300 rounded-lg hover:border-blue-300 transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        <span className="text-sm">Copy</span>
                      </button>
                    )}
                  </div>

                  {/* Time Slots */}
                  {daySlots.length > 0 && (
                    <div className="space-y-3">
                      {daySlots.map(slot => (
                        <div
                          key={slot.id}
                          className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4 text-gray-600" />
                                <span className="font-medium text-gray-800">
                                  {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                </span>
                              </div>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {slot.label}
                              </span>
                            </div>
                            {slot.notes && (
                              <p className="text-sm text-gray-600 mt-1">{slot.notes}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openEditSlotModal(day.key, slot)}
                              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteTimeSlot(day.key, slot.id)}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Time Blocking Tab */}
      {activeTab === 'blocking' && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Plane className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-800">
                  {blockedPeriods.length} Blocked Period{blockedPeriods.length !== 1 ? 's' : ''}
                </p>
                <p className="text-sm text-orange-700">
                  Manage vacation, out-of-office, and other unavailable periods
                </p>
              </div>
            </div>
          </div>

          {/* Add Block Button */}
          <div className="flex justify-end">
            <button
              onClick={openAddBlockModal}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Block Period</span>
            </button>
          </div>

          {/* Blocked Periods List */}
          {blockedPeriods.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Plane className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No blocked periods set</h3>
              <p className="text-gray-500 mb-6">
                Block out vacation days, conferences, or other times when you're unavailable
              </p>
              <button
                onClick={openAddBlockModal}
                className="flex items-center space-x-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Add Your First Block</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {blockedPeriods.map(block => (
                <div
                  key={block.id}
                  className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="flex items-center space-x-2">
                          {getBlockTypeIcon(block.type)}
                          <h3 className="font-semibold text-gray-800">{block.title}</h3>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${getBlockTypeColor(block.type)}`}>
                          {block.type.replace('-', ' ').toUpperCase()}
                        </span>
                        {block.recurring && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                            RECURRING
                          </span>
                        )}
                      </div>

                      <div className="flex items-center flex-wrap gap-2 text-sm text-gray-600 mb-2">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(block.startDate)}</span>
                          {/* Show time if not all-day */}
                          {block.allDay === false && block.startTime && (
                            <span className="text-orange-600 font-medium ml-1">
                              {formatTime(block.startTime)}
                            </span>
                          )}
                        </div>
                        <span>→</span>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(block.endDate)}</span>
                          {/* Show time if not all-day */}
                          {block.allDay === false && block.endTime && (
                            <span className="text-orange-600 font-medium ml-1">
                              {formatTime(block.endTime)}
                            </span>
                          )}
                        </div>
                        {/* Show "All Day" or "Partial Day" badge */}
                        {block.allDay === false ? (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                            Partial Day
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            All Day
                          </span>
                        )}
                      </div>

                      {block.reason && (
                        <p className="text-sm text-gray-600 mt-2">
                          <span className="font-medium">Reason:</span> {block.reason}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openEditBlockModal(block)}
                        className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteBlockedPeriod(block.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Time Slot Modal */}
      {showTimeSlotModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-800">
                  {editingSlot ? 'Edit Time Slot' : 'Add Time Slot'}
                </h3>
                <button
                  onClick={() => {
                    setShowTimeSlotModal(false);
                    setEditingSlot(null);
                    resetTimeSlotForm();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Day</label>
                  <select
                    value={selectedDay}
                    onChange={(e) => setSelectedDay(e.target.value)}
                    disabled={editingSlot !== null}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    {daysOfWeek.map(day => (
                      <option key={day.key} value={day.key}>{day.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                    <input
                      type="time"
                      value={timeSlotForm.startTime}
                      onChange={(e) => setTimeSlotForm(prev => ({ ...prev, startTime: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                    <input
                      type="time"
                      value={timeSlotForm.endTime}
                      onChange={(e) => setTimeSlotForm(prev => ({ ...prev, endTime: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                  <select
                    value={timeSlotForm.label}
                    onChange={(e) => setTimeSlotForm(prev => ({ ...prev, label: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {commonTimeSlots.map(slot => (
                      <option key={slot.label} value={slot.label}>{slot.label}</option>
                    ))}
                    <option value="Available">Available</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                  <textarea
                    value={timeSlotForm.notes}
                    onChange={(e) => setTimeSlotForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Any additional notes about this time slot..."
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => {
                    setShowTimeSlotModal(false);
                    setEditingSlot(null);
                    resetTimeSlotForm();
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingSlot ? updateTimeSlot : addTimeSlot}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>{editingSlot ? 'Update' : 'Add'} Time Slot</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blocked Period Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-800">
                  {editingBlock ? 'Edit Blocked Period' : 'Add Blocked Period'}
                </h3>
                <button
                  onClick={() => {
                    setShowBlockModal(false);
                    setEditingBlock(null);
                    resetBlockForm();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title (Optional)</label>
                  <input
                    type="text"
                    value={blockForm.title}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Summer Vacation, Medical Conference"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Block Type</label>
                  <select
                    value={blockForm.type}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="vacation">Vacation</option>
                    <option value="out-of-office">Out of Office</option>
                    <option value="conference">Conference</option>
                    <option value="personal">Personal</option>
                  </select>
                </div>

                {/* All Day Toggle */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="allDay"
                    checked={blockForm.allDay}
                    onChange={(e) => setBlockForm(prev => ({
                      ...prev,
                      allDay: e.target.checked,
                      // Reset times when switching to all-day
                      startTime: e.target.checked ? '' : prev.startTime,
                      endTime: e.target.checked ? '' : prev.endTime
                    }))}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="allDay" className="ml-2 block text-sm text-gray-700">
                    All day block (entire day unavailable)
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={blockForm.startDate}
                      onChange={(e) => setBlockForm(prev => ({
                        ...prev,
                        startDate: e.target.value,
                        // For single-day time blocks, set end date to match start date
                        endDate: !prev.allDay && !prev.endDate ? e.target.value : prev.endDate
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={blockForm.endDate}
                      onChange={(e) => setBlockForm(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>

                {/* Time pickers - only show when not all-day */}
                {!blockForm.allDay && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                      <input
                        type="time"
                        value={blockForm.startTime}
                        onChange={(e) => setBlockForm(prev => ({ ...prev, startTime: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                      <input
                        type="time"
                        value={blockForm.endTime}
                        onChange={(e) => setBlockForm(prev => ({ ...prev, endTime: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason (Optional)</label>
                  <textarea
                    value={blockForm.reason}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, reason: e.target.value }))}
                    rows={3}
                    placeholder="Additional details about this blocked period..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="recurring"
                    checked={blockForm.recurring}
                    onChange={(e) => setBlockForm(prev => ({ ...prev, recurring: e.target.checked }))}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="recurring" className="ml-2 block text-sm text-gray-700">
                    Recurring annually (e.g., annual conference)
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => {
                    setShowBlockModal(false);
                    setEditingBlock(null);
                    resetBlockForm();
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingBlock ? updateBlockedPeriod : addBlockedPeriod}
                  disabled={!blockForm.startDate || !blockForm.endDate}
                  className="flex items-center space-x-2 px-6 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>{editingBlock ? 'Update' : 'Add'} Block</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(AvailabilityView);